import React, { useState, useEffect, useCallback } from "react";
import { getApiUrl } from "../../../../../../../../../config/api";

// ── Constants ─────────────────────────────────────────────────────────────────

const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "zh", label: "中文" },
    { code: "tw", label: "臺灣" },
    { code: "jp", label: "日本語" },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function PinBadge({ isPinned, versionLabel }) {
    return isPinned ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 text-xs font-medium border border-orange-200">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
            </svg>
            Pinned → {versionLabel}
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium border border-gray-200">
            Latest
        </span>
    );
}

/** Collapsed product row — shows name, version count, pin status */
function ProductRow({ product, langCode, onClick }) {
    const name = product.model_id || `Product #${product.product_id}`;
    const category = product.category_name || null;

    return (
        <button
            onClick={onClick}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors rounded-xl group text-left"
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-800 truncate">
                        {name}
                    </span>
                    {category && (
                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md border border-gray-200">
                            {category}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-400 font-mono">
                        ID {product.product_id}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="text-xs text-gray-500">
                        {product.version_count} {langCode} version
                        {product.version_count !== 1 ? "s" : ""}
                    </span>
                </div>
            </div>

            <PinBadge
                isPinned={product.is_pinned}
                versionLabel={product.pinned_version_number}
            />

            <svg
                className="w-4 h-4 text-gray-300 group-hover:text-orange-400 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                />
            </svg>
        </button>
    );
}

/** Expanded version picker for a single product */
function VersionPicker({
    product,
    langCode,
    channelId,
    currentUser,
    onDone,
    onBack,
}) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(
        product.pinned_version_id || null,
    );
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [unpinConfirm, setUnpinConfirm] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(
                    `${getApiUrl()}/api/v2/products/${product.product_id}/versions`,
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                const data = await res.json();
                if (data.success) {
                    const filtered = (data.data || []).filter((v) =>
                        v.version_number?.startsWith(`${langCode}-`),
                    );
                    setVersions(filtered);
                } else {
                    setError(data.error || "Failed to load versions.");
                }
            } catch (err) {
                setError(err.message || "Failed to load versions.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [product.product_id, langCode, currentUser]);

    const handlePin = async () => {
        if (!selectedId) return;
        setSaving(true);
        setSaveError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(
                `${getApiUrl()}/api/v2/channel-visibility/${channelId}/pins`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        product_id: product.product_id,
                        version_id: selectedId,
                        lang_code: langCode,
                    }),
                },
            );
            const data = await res.json();
            if (data.success) {
                onDone();
            } else {
                setSaveError(data.error || "Failed to pin version.");
            }
        } catch (err) {
            setSaveError(err.message || "Failed to pin version.");
        } finally {
            setSaving(false);
        }
    };

    const handleUnpin = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(
                `${getApiUrl()}/api/v2/channel-visibility/${channelId}/pins/${product.product_id}?lang=${langCode}`,
                {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            const data = await res.json();
            if (data.success) {
                onDone();
            } else {
                setSaveError(data.error || "Failed to unpin.");
            }
        } catch (err) {
            setSaveError(err.message || "Failed to unpin.");
        } finally {
            setSaving(false);
            setUnpinConfirm(false);
        }
    };

    const name = product.model_id || `Product #${product.product_id}`;

    return (
        <div className="flex flex-col h-full">
            {/* Header with back button */}
            <div className="flex items-center gap-3 px-6 pt-4 pb-3 border-b border-gray-100 flex-shrink-0">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    Back
                </button>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                        {name}
                    </p>
                    <p className="text-xs text-gray-400">
                        Select a <span className="font-mono">{langCode}</span>{" "}
                        version to pin for this channel
                    </p>
                </div>
                {product.is_pinned && (
                    <PinBadge
                        isPinned
                        versionLabel={product.pinned_version_number}
                    />
                )}
            </div>

            {/* Versions list */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
                {loading && (
                    <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
                        <svg
                            className="w-5 h-5 animate-spin text-orange-400"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                        </svg>
                        <span className="text-sm">Loading versions…</span>
                    </div>
                )}

                {!loading && error && (
                    <p className="text-xs text-red-500 text-center py-8">
                        {error}
                    </p>
                )}

                {!loading && !error && versions.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-8">
                        No <span className="font-mono">{langCode}</span>{" "}
                        versions found.
                    </p>
                )}

                {!loading &&
                    !error &&
                    versions.map((v) => (
                        <label
                            key={v.version_id}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                                selectedId === v.version_id
                                    ? "border-orange-400 bg-orange-50 text-orange-700"
                                    : "border-gray-200 bg-white hover:border-gray-300 text-gray-700"
                            }`}
                        >
                            <input
                                type="radio"
                                name="pin-version"
                                value={v.version_id}
                                checked={selectedId === v.version_id}
                                onChange={() => setSelectedId(v.version_id)}
                                className="accent-orange-500"
                            />
                            <div className="flex-1 min-w-0">
                                <span className="text-sm font-mono font-medium">
                                    {v.version_number}
                                </span>
                                {v.created_at && (
                                    <span className="ml-2 text-xs text-gray-400">
                                        {new Date(
                                            v.created_at,
                                        ).toLocaleDateString()}
                                    </span>
                                )}
                            </div>
                            {v.version_id === product.pinned_version_id && (
                                <span className="text-xs text-orange-400 font-medium">
                                    current pin
                                </span>
                            )}
                        </label>
                    ))}
            </div>

            {saveError && (
                <p className="text-xs text-red-500 px-6 pb-2">{saveError}</p>
            )}

            {/* Footer actions */}
            <div className="flex items-center gap-2 px-6 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
                {product.is_pinned && !unpinConfirm && (
                    <button
                        onClick={() => setUnpinConfirm(true)}
                        className="px-3 py-2 text-xs font-medium text-red-500 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-all"
                    >
                        Remove pin
                    </button>
                )}
                {unpinConfirm && (
                    <>
                        <span className="text-xs text-red-600">
                            Unpin? Will fall back to latest.
                        </span>
                        <button
                            onClick={() => setUnpinConfirm(false)}
                            className="px-2.5 py-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUnpin}
                            disabled={saving}
                            className="px-2.5 py-1.5 text-xs text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all disabled:opacity-40"
                        >
                            {saving ? "Removing…" : "Confirm"}
                        </button>
                    </>
                )}
                <div className="flex-1" />
                <button
                    onClick={handlePin}
                    disabled={
                        !selectedId ||
                        saving ||
                        selectedId === product.pinned_version_id
                    }
                    className="px-4 py-2 text-xs font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {saving ? "Saving…" : "Pin this version"}
                </button>
            </div>
        </div>
    );
}

// ── Main Export ────────────────────────────────────────────────────────────────

export default function ManualPinningContent({
    channel,
    language,
    currentUser,
    activeLang,
    setActiveLang,
}) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [expandedProduct, setExpandedProduct] = useState(null);
    const [search, setSearch] = useState("");

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(
                `${getApiUrl()}/api/v2/channel-visibility/${channel.channel_id}/pinnable-products?lang=${activeLang}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const data = await res.json();
            if (data.success) {
                setProducts(data.data || []);
            } else {
                setFetchError(data.error || "Failed to load products.");
            }
        } catch (err) {
            setFetchError(err.message || "Failed to load products.");
        } finally {
            setLoading(false);
        }
    }, [channel.channel_id, activeLang, currentUser]);

    useEffect(() => {
        setExpandedProduct(null);
        setSearch("");
        fetchProducts();
    }, [fetchProducts]);

    const pinnedCount = products.filter((p) => p.is_pinned).length;

    const filtered = products.filter((p) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const name = (p.model_id || String(p.product_id)).toLowerCase();
        return name.includes(q) || String(p.product_id).includes(q);
    });

    // ── If a product is expanded, show the version picker ──
    if (expandedProduct) {
        return (
            <VersionPicker
                product={expandedProduct}
                langCode={activeLang}
                channelId={channel.channel_id}
                currentUser={currentUser}
                onDone={() => {
                    setExpandedProduct(null);
                    fetchProducts();
                }}
                onBack={() => setExpandedProduct(null)}
            />
        );
    }

    // ── Product list view ──
    return (
        <>
            {/* ── Language Tabs ───────────────────────────────────────── */}
            <div className="flex gap-1 px-6 pt-4 pb-2 flex-shrink-0 border-b border-gray-100 overflow-x-auto">
                {LANGUAGES.map((lang) => (
                    <button
                        key={lang.code}
                        onClick={() => setActiveLang(lang.code)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                            activeLang === lang.code
                                ? "bg-orange-500 text-white shadow-sm"
                                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        }`}
                    >
                        <span>{lang.label}</span>
                    </button>
                ))}

                {pinnedCount > 0 && !loading && (
                    <span className="ml-auto flex-shrink-0 self-center px-2.5 py-1 bg-orange-50 text-orange-600 text-xs font-medium rounded-full border border-orange-200">
                        {pinnedCount} pinned
                    </span>
                )}
            </div>

            {/* ── Search ─────────────────────────────────────────────── */}
            <div className="px-6 py-3 flex-shrink-0">
                <div className="relative">
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search products…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* ── Body ───────────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-1.5">
                {loading && (
                    <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
                        <svg
                            className="w-5 h-5 animate-spin text-orange-400"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                        </svg>
                        <span className="text-sm">Loading products…</span>
                    </div>
                )}

                {!loading && fetchError && (
                    <div className="flex flex-col items-center gap-2 py-12">
                        <p className="text-red-500 text-sm">{fetchError}</p>
                        <button
                            onClick={fetchProducts}
                            className="text-xs text-orange-500 underline"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {!loading && !fetchError && filtered.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                        <svg
                            className="w-10 h-10 mb-3 opacity-30"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                        </svg>
                        <p className="text-sm">
                            {search
                                ? "No products match your search."
                                : "No products with multiple versions for this language."}
                        </p>
                    </div>
                )}

                {!loading &&
                    !fetchError &&
                    filtered.map((product) => (
                        <ProductRow
                            key={product.product_id}
                            product={product}
                            langCode={activeLang}
                            onClick={() => setExpandedProduct(product)}
                        />
                    ))}
            </div>

            {/* ── Footer ─────────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
                <p className="text-xs text-gray-400">
                    {!loading &&
                        `${filtered.length} product${filtered.length !== 1 ? "s" : ""} with multiple versions · ${pinnedCount} pinned`}
                </p>
            </div>
        </>
    );
}
